import { memo } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  OnDragStartResponder,
  ResponderProvided,
} from 'react-beautiful-dnd';
import styled from 'styled-components';

import { SortableOptionPill, SortableOptionPillItem } from './SortableOptionPill';

const Wrapper = styled.div<{ isDraggingOver: boolean; isDropDisabled: boolean; isDraggingFrom: boolean }>`
  display: flex;
  background-color: transparent;
  flex-direction: column;
  opacity: ${({ isDropDisabled }) => (isDropDisabled ? 0.5 : `inherit`)};
  transition: background-color 0.2s ease, opacity 0.1s ease;
`;

interface InnerQuoteListProps {
  fields: SortableOptionPillItem[];
}

const InnerFieldList = memo(({ fields }: InnerQuoteListProps) => {
  return (
    <>
      {fields.map((field: SortableOptionPillItem, index: number) => (
        <Draggable key={field.id} draggableId={field.id} index={index}>
          {(dragProvided: DraggableProvided, dragSnapshot: DraggableStateSnapshot) => (
            <SortableOptionPill
              key={field.id}
              field={field}
              isDragging={dragSnapshot.isDragging}
              isGroupedOver={Boolean(dragSnapshot.combineTargetFor)}
              provided={dragProvided}
            />
          )}
        </Draggable>
      ))}
    </>
  );
});

interface InnerListProps {
  dropProvided: DroppableProvided;
  fields: Array<SortableOptionPillItem>;
}

const InnerList = ({ fields, dropProvided }: InnerListProps) => {
  return (
    <div ref={dropProvided.innerRef}>
      <InnerFieldList fields={fields} />
      {dropProvided.placeholder}
    </div>
  );
};

interface SortableOptionContainerProps {
  listId: string;
  fields: SortableOptionPillItem[];
  isDropDisabled?: boolean;
  onDragStart?: OnDragStartResponder | undefined;
  onDragEnd: (fieldId: string, index?: number) => void;
}

export const SortableOptionContainer = ({
  listId,
  fields,
  isDropDisabled = false,
  onDragStart,
  onDragEnd,
}: SortableOptionContainerProps) => {
  return (
    <DragDropContext
      onDragStart={onDragStart}
      onDragEnd={(result: DropResult, provided: ResponderProvided) => {
        onDragEnd(result.draggableId, result.destination?.index);
      }}
    >
      <Droppable
        droppableId={listId}
        type="card"
        isDropDisabled={isDropDisabled}
        renderClone={(provided, snapshot, descriptor) => (
          <SortableOptionPill
            field={fields[descriptor.source.index]}
            provided={provided}
            isDragging={snapshot.isDragging}
          />
        )}
      >
        {(dropProvided: DroppableProvided, dropSnapshot: DroppableStateSnapshot) => (
          <Wrapper
            isDraggingOver={dropSnapshot.isDraggingOver}
            isDropDisabled={isDropDisabled}
            isDraggingFrom={Boolean(dropSnapshot.draggingFromThisWith)}
            {...dropProvided.droppableProps}
          >
            <InnerList fields={fields} dropProvided={dropProvided} />
          </Wrapper>
        )}
      </Droppable>
    </DragDropContext>
  );
};
